import React, { createContext, useContext, useMemo, useState, ReactNode } from 'react';
import { CallDetails } from '../types/Call';
import type { UseCadRecord } from '../hooks/useCadRecord';

/** Which dataset the chatbot answers over. */
export type ChatScope = 'call' | 'all' | 'results';

/**
 * Mirrors CallDatasetDigestService.MaxScopeCallIds on the server, which truncates
 * anyway — capping here keeps the UI labels honest about what is analyzed.
 */
export const MAX_CHAT_SCOPE_CALLS = 50;

export interface ResultsScopeCall {
  callId: string;
  incidentNumber: string;
}

/** The filtered result set currently shown on the home page, published for the chatbot. */
export interface ResultsScope {
  /** Capped at MAX_CHAT_SCOPE_CALLS — the calls actually sent to the assistant. */
  calls: ResultsScopeCall[];
  /** Rows in the viewed result page before capping, so labels can say "first 50 of N". */
  totalCount: number;
}

/**
 * The call page's CAD record, published while the page is open and the agency has CAD records on,
 * so the assistant's CAD entry point (leg 5) drives the same record, the same poll and the same
 * audited reads as the CAD Record tab rather than a second copy of them.
 */
export interface CadChatApi {
  cad: UseCadRecord;
  /** Supervisor or TenantAdmin: may attach, request the check and apply. */
  canEdit: boolean;
  /** Re-reads the call after an apply, as the tab does; see CallDetailsPage. */
  onCallRecordApplied: (operatorChanged: boolean) => Promise<void>;
}

interface CallContextType {
  callContext?: {
    callDetails: CallDetails;
    transcription: string;
  };
  setCallContext: (
    context: { callDetails: CallDetails; transcription: string } | undefined
  ) => void;
  cadChat?: CadChatApi;
  setCadChat: (api: CadChatApi | undefined) => void;
  resultsScope?: ResultsScope;
  setResultsScope: (scope: ResultsScope | undefined) => void;
  chatScope: ChatScope;
  setChatScope: (scope: ChatScope) => void;
  isChatbotOpen: boolean;
  setIsChatbotOpen: (open: boolean) => void;
}

const CallContext = createContext<CallContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useCallContext = () => {
  const context = useContext(CallContext);
  if (!context) {
    throw new Error('useCallContext must be used within a CallContextProvider');
  }
  return context;
};

interface CallContextProviderProps {
  children: ReactNode;
}

export const CallContextProvider: React.FC<CallContextProviderProps> = ({ children }) => {
  const [callContext, setCallContext] = useState<
    | {
        callDetails: CallDetails;
        transcription: string;
      }
    | undefined
  >();
  const [cadChat, setCadChat] = useState<CadChatApi | undefined>();
  const [resultsScope, setResultsScope] = useState<ResultsScope | undefined>();
  const [chatScope, setChatScope] = useState<ChatScope>('all');
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);

  // One value per distinct state: the CAD record's poll (leg 5) updates cadChat every few
  // seconds, and a fresh object here would re-render every consumer on every tick.
  const value = useMemo(
    () => ({
      callContext,
      setCallContext,
      cadChat,
      setCadChat,
      resultsScope,
      setResultsScope,
      chatScope,
      setChatScope,
      isChatbotOpen,
      setIsChatbotOpen,
    }),
    [callContext, cadChat, resultsScope, chatScope, isChatbotOpen]
  );

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
};
